import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "resend-email" }
const _hoisted_2 = { class: "rich-editor-wrap rich-editor-wrap--small" }

import { computed, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { request } from '@/config/request/interceptors'
import { t } from '@/plugins/i18n'
import { ShopEmailHistoryRespDTO, ShopEmailSendReqDTO } from '@/service/customer'
import { useEmailSetting } from '@/util/moreRequest'

type SendEmailDrawerRow = ShopEmailHistoryRespDTO & Pick<ShopEmailSendReqDTO, 'orderNo' | 'items' | 'variableMap'>
type PlatformOrderEmailPreviewPayload = {
  customerShopId?: number
  platformOrderNo?: string
  orderNo?: string
  variableMap?: ShopEmailSendReqDTO['variableMap']
  templateScene?: string
  subject?: string
  content?: string
  items?: ShopEmailSendReqDTO['items']
}

export default /*#__PURE__*/_defineComponent({
  emits: ['success'],
  setup(__props, { expose, emit }) {


const [emailSetting] = useEmailSetting()
/** 邮件模板场景固定排序，保证下拉展示顺序稳定。 */
const templateSceneSortMap: Record<string, number> = {
  NORMAL: 1,
  PARTIAL: 2,
  FINAL: 3
}
const visible = ref(false)
const sendLoading = ref(false)
const previewLoading = ref(false)
const currentRow = ref<SendEmailDrawerRow>({})
const formRef = ref<FormInstance>()
const previewRequestSeq = ref(0)
const toolbarOptions = [
  [{ header: [1, 2, false] }],
  ['bold', 'italic', 'underline'],
  [{ color: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean']
]
const form = reactive({
  historyId: undefined as number | undefined,
  platformOrderNo: '',
  shopifyOrderNo: '',
  shopifyOrderName: '',
  recipientEmail: '',
  templateId: undefined as number | undefined,
  templateName: '',
  templateScene: '',
  subject: '',
  content: ''
})
const rules: FormRules = {
  recipientEmail: [
    { required: true, message: t('メールアドレスを入力してください'), trigger: 'blur' }
  ],
  subject: [
    { required: true, message: t('件名を入力してください'), trigger: 'blur' }
  ],
  content: [
    { required: true, validator: validateContent, trigger: 'change' }
  ]
}

/** 按 NORMAL、PARTIAL、FINAL 顺序排序后的模板列表。 */
const sortedTemplateList = computed(() => {
  /** 当前接口返回的模板列表副本。 */
  const templateList = Array.isArray(emailSetting.value?.templateList) ? [...emailSetting.value.templateList] : []
  return templateList.sort((prev, next) => {
    return (templateSceneSortMap[prev.templateScene || ''] || Number.MAX_SAFE_INTEGER) -
      (templateSceneSortMap[next.templateScene || ''] || Number.MAX_SAFE_INTEGER)
  })
})

const open = (row: SendEmailDrawerRow) => {
  currentRow.value = { ...row }
  form.historyId = String(row.id) === '-' ? '' : row.id
  form.platformOrderNo = row.platformOrderNo || ''
  form.shopifyOrderNo = row.shopifyOrderNo || row.shopifyOrderName || ''
  form.shopifyOrderName = row.shopifyOrderName || ''
  form.recipientEmail = row.recipientEmail || ''
  form.templateId = String(row.templateId) === '-' ? undefined : row.templateId
  // form.templateName = row.templateName || ''
  form.templateScene = row.templateScene || ''
  form.subject = row.subject || ''
  form.content = ensureHtml(row.content || '')
  visible.value = true
  syncShopifyOrderNoToSubject(row.shopifyOrderNo || row.shopifyOrderName || '')
}
const close = () => {
  visible.value = false
}
const syncShopifyOrderNoToSubject = (shopifyOrderNo: string) => {
  if (!form.subject || !/\[[^\]]*\]/.test(form.subject)) return
  form.subject = form.subject.replace(/\[[^\]]*\]/, `[${shopifyOrderNo || ''}]`)
}

/** 切换模板后通过正则把 Shopify 订单号替换到件名中括号里。 */
const replaceSubjectOrderNo = (subject: string, shopifyOrderNo: string) => {
  if (!subject || !/\[[^\]]*\]/.test(subject)) return subject
  return subject.replace(/\[[^\]]*\]/, `[${shopifyOrderNo || ''}]`)
}

/** 合并当前弹窗输入的 Shopify 订单号，保证模板重算时使用最新 variableMap。 */
const buildPreviewVariableMap = () => {
  /** 当前订单原始预览变量。 */
  const currentVariableMap = currentRow.value.variableMap || {}
  /** 当前输入框里的 Shopify 订单号。 */
  const shopifyOrderNo = form.shopifyOrderNo || form.shopifyOrderName || ''
  return {
    ...currentVariableMap,
    order_no: shopifyOrderNo || currentVariableMap.order_no,
    shopify_order_name: shopifyOrderNo || currentVariableMap.shopify_order_name
  }
}

const handleTemplateSceneChange = async (templateScene: string) => {
  const template = sortedTemplateList.value.find(item => item.templateScene === templateScene)
  /** 当前模板切换时使用的 Shopify 订单号。 */
  const shopifyOrderNo = form.shopifyOrderNo || form.shopifyOrderName || ''
  /** 模板切换后提交给预览接口的原始件名。 */
  const subject = template?.subject || form.subject
  const content = template?.content || form.content
  /** 当前模板切换后重新预览使用的变量。 */
  const previewVariableMap = buildPreviewVariableMap()
  const currentSeq = previewRequestSeq.value + 1
  previewRequestSeq.value = currentSeq
  previewLoading.value = true
  try {
    const res = await request.order.customer.platformOrderEmailPreview({
      customerShopId: currentRow.value.customerShopId,
      platformOrderNo: form.platformOrderNo,
      orderNo: currentRow.value.orderNo,
      variableMap: previewVariableMap,
      templateScene,
      subject,
      content,
      items: currentRow.value.items
    } as PlatformOrderEmailPreviewPayload)
    if (currentSeq !== previewRequestSeq.value || !res.success) return
    currentRow.value = {
      ...currentRow.value,
      templateScene,
      templateId: template?.id,
      templateName: template?.templateName,
      subject: replaceSubjectOrderNo(res.data?.subject || subject, shopifyOrderNo),
      content: res.data?.content || content,
      deliveryTaskCode: res.data?.deliveryTaskCode || currentRow.value.deliveryTaskCode,
      itemLoopContent: res.data?.itemLoopContent || currentRow.value.itemLoopContent,
      items: res.data?.items || currentRow.value.items,
      variableMap: res.data?.variableMap || previewVariableMap
    }
    form.templateScene = templateScene
    form.templateId = template?.id
    form.templateName = template?.templateName || ''
    form.subject = currentRow.value.subject || ''
    form.content = ensureHtml(currentRow.value.content || '')
  } finally {
    if (currentSeq === previewRequestSeq.value) {
      previewLoading.value = false
    }
  }
}
const handleSend = async () => {
  if (sendLoading.value) return
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) return
  sendLoading.value = true
  try {
    /** 当前再送信使用的变量，和模板切换预览保持一致。 */
    const previewVariableMap = buildPreviewVariableMap()
    const res = await request.customer.aws.shopEmailSend({
      historyId: form.historyId,
      businessKey: currentRow.value.businessKey,
      platformOrderNo: form.platformOrderNo,
      shopifyOrderNo: form.shopifyOrderNo,
      shopifyOrderName: form.shopifyOrderName,
      recipientEmail: form.recipientEmail,
      templateId: form.templateId || undefined,
      templateScene: form.templateScene,
      subject: form.subject,
      content: form.content,
      orderNo: currentRow.value.orderNo,
      items: currentRow.value.items,
      variableMap: previewVariableMap,
      mainCustomerId: currentRow.value.mainCustomerId,
      orderId: currentRow.value.orderId,
      shipmentId: currentRow.value.shipmentId,
      deliveryTaskCode: currentRow.value.deliveryTaskCode,
      customerShopId: currentRow.value.customerShopId
    })
    if (res.success) {
      ElMessage.success(t('再送信しました'))
      close()
      emit('success')
    }
  } finally {
    sendLoading.value = false
  }
}
const getTemplateSceneName = (scene?: string) => {
  const sceneMap: Record<string, string> = {
    NORMAL: t('通常出荷メール'),
    PARTIAL: t('一部分納出荷メール'),
    FINAL: t('最終出荷メール')
  }
  return scene ? sceneMap[scene] || scene : ''
}
const ensureHtml = (value: string) => {
  if (!value) return ''
  return /<\/?[a-z][\s\S]*>/i.test(value) ? value : plainToHtml(value)
}
const plainToHtml = (value: string) => {
  return value
    .split(/\n{2,}/)
    .map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('')
}
const escapeHtml = (value: string) => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
function validateContent (_rule: unknown, value: string, callback: (error?: Error) => void) {
  if (getPlainText(value)) {
    callback()
    return
  }
  callback(new Error(t('本文を入力してください')))
}
const getPlainText = (value: string) => {
  return String(value || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, '')
}

expose({
  open
})

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_drawer = _resolveComponent("el-drawer")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_component_el_drawer, {
    modelValue: visible.value,
    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((visible).value = $event)),
    title: _unref(t)('メール再送信'),
    "custom-class": "send-email-resend-drawer",
    size: "50%",
    "append-to-body": "",
    "destroy-on-close": ""
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_el_button, {
        plain: "",
        onClick: close
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('キャンセル')), 1)
        ]),
        _: 1
      }),
      _createVNode(_component_el_button, {
        type: "primary",
        loading: sendLoading.value,
        onClick: handleSend
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('再送信')), 1)
        ]),
        _: 1
      }, 8, ["loading"])
    ]),
    default: _withCtx(() => [
      _withDirectives(_createElementVNode("div", _hoisted_1, [
        _createVNode(_component_el_form, {
          ref: (_value, _refs) => {
            _refs['formRef'] = _value
            formRef.value = _value
          },
          model: _unref(form),
          rules: rules,
          "label-width": "140px",
          class: "resend-email-form"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: _unref(t)('注文(Shopify)')
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: _unref(form).shopifyOrderNo,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(form).shopifyOrderNo) = $event)),
                  onInput: syncShopifyOrderNoToSubject
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_component_el_form_item, {
              label: _unref(t)('宛先'),
              prop: "recipientEmail"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: _unref(form).recipientEmail,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(form).recipientEmail) = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }, 8, ["label"]),
            (_unref(form).templateScene)
              ? (_openBlock(), _createBlock(_component_el_form_item, {
                  key: 0,
                  label: _unref(t)('テンプレート')
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: _unref(form).templateScene,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(form).templateScene) = $event)),
                      style: {"width":"100%"},
                      loading: previewLoading.value,
                      onChange: handleTemplateSceneChange
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(sortedTemplateList), (template) => {
                          return (_openBlock(), _createBlock(_component_el_option, {
                            key: template.templateScene,
                            label: template.templateName,
                            value: template.templateScene!
                          }, null, 8, ["label", "value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["modelValue", "loading"])
                  ]),
                  _: 1
                }, 8, ["label"]))
              : _createCommentVNode("", true),
            _createVNode(_component_el_form_item, {
              label: _unref(t)('件名'),
              prop: "subject"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: _unref(form).subject,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(form).subject) = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_component_el_form_item, {
              label: _unref(t)('本文'),
              prop: "content",
              class: "rich-editor-item"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_unref(QuillEditor), {
                    content: _unref(form).content,
                    "onUpdate:content": _cache[4] || (_cache[4] = ($event: any) => ((_unref(form).content) = $event)),
                    class: "rich-editor",
                    "content-type": "html",
                    theme: "snow",
                    toolbar: toolbarOptions
                  }, null, 8, ["content"])
                ])
              ]),
              _: 1
            }, 8, ["label"])
          ]),
          _: 1
        }, 8, ["model"])
      ], 512), [
        [_directive_loading, previewLoading.value]
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})